/** Copyright 2024 SAP SE or an SAP affiliate company. All rights reserved. */

import { useTranslations } from '@/hooks/useTranslations';
import { Button, Dialog, FlexBox, Form, FormItem, Input, Label, Text, Title, MessageBoxType, MessageBox, Link } from '@ui5/webcomponents-react';
import { useCallback, useState } from 'react';
import { ThemingParameters } from '@ui5/webcomponents-react-base';
import useOpen from '@/hooks/useOpen';
import { useAppDispatch } from '@/hooks';
import { loadingActions } from '@/store/loadingSlice';
import axios from 'axios';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import RegexCollection from '@/utils/RegexCollection';
import { z } from 'zod';

interface SignUpUser {
    firstName: string;
    lastName: string;
    email: string;
}

const signUpSchema = z.object({
    firstName: z.string().min(1).regex(RegexCollection.alphabetic),
    lastName: z.string().min(1).regex(RegexCollection.alphabetic),
    email: z.string().min(1).email(),
});

interface SignUpMessage {
    type: MessageBoxType;
    title: string;
    subTitle: string;
    statusCode?: number;
}

type Props = {
    onClose: VoidFunction;
};

const signUpMessages: { [key: number]: SignUpMessage } = {
    201: {
        type: MessageBoxType.Success,
        title: 'SUCCESSFUL_SIGNUP_TITLE',
        subTitle: 'SUCCESSFUL_SIGNUP_SUBTITLE',
        statusCode: 201,
    },
    404: {
        type: MessageBoxType.Error,
        title: 'EMAIL_NOT_FOUND_TITLE',
        subTitle: 'EMAIL_NOT_FOUND_SUBTITLE',
        statusCode: 404,
    },
    409: {
        type: MessageBoxType.Error,
        title: 'EMAIL_ALREADY_EXISTS_TITLE',
        subTitle: 'EMAIL_ALREADY_EXISTS_SUBTITLE',
        statusCode: 409,
    },
};

const SignUp = ({ onClose }: Props) => {
    const { t } = useTranslations();
    const [isOpen, toggle] = useOpen();
    const dispatch = useAppDispatch();
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<z.infer<typeof signUpSchema>>({
        defaultValues: {
            firstName: '',
            lastName: '',
            email: '',
        },
        mode: 'onBlur',
        resolver: zodResolver(signUpSchema),
    });

    const onDialogKeydown = useCallback((k: React.KeyboardEvent<HTMLElement>) => {
        if (k.code === 'Escape') onClose();
    }, []);

    const [signUpMessage, setSignUpMessage] = useState<SignUpMessage>({
        type: MessageBoxType.Success,
        title: '',
        subTitle: '',
    });

    const onSubmit = handleSubmit(async (data: SignUpUser) => {
        dispatch(loadingActions.startLoading());
        try {
            const response = await axios.post(`/fre_api/v1/ias/user`, {
                givenName: data.firstName,
                familyName: data.lastName,
                userType: 'customer',
                active: true,
                email: data.email,
            });
            setSignUpMessage(signUpMessages[response.status]);
        } catch (e: any) {
            setSignUpMessage(signUpMessages[e.response.status]);
        } finally {
            dispatch(loadingActions.stopLoading());
            toggle();
        }
    });

    const signUpForm = (
        <Form labelSpan="S4">
            <FlexBox direction="Column" gap="1rem" style={{ width: '100%' }}>
                <FormItem
                    labelContent={
                        <Label required showColon>
                            {t('FIRST_NAME_LABEL')}
                        </Label>
                    }
                    style={{ textAlign: 'right', marginRight: '2rem' }}
                >
                    <Input
                        required
                        aria-label="First Name"
                        aria-required
                        {...register('firstName')}
                        valueState={errors.firstName ? 'Negative' : 'None'}
                        valueStateMessage={<Text>{t('INVALID_FIRST_NAME_MESSAGE')}</Text>}
                        placeholder={t('FIRST_NAME')}
                        style={{ height: '30px' }}
                    />
                </FormItem>

                <FormItem
                    labelContent={
                        <Label required showColon>
                            {t('LAST_NAME')}
                        </Label>
                    }
                    style={{ textAlign: 'right', marginRight: '2rem' }}
                >
                    <Input
                        required
                        aria-label="Last Name"
                        aria-required
                        {...register('lastName')}
                        valueState={errors.lastName ? 'Negative' : 'None'}
                        valueStateMessage={<Text>{t('INVALID_FAMILY_NAME_MESSAGE')}</Text>}
                        placeholder={t('LAST_NAME')}
                        style={{ height: '30px' }}
                    />
                </FormItem>

                <FormItem
                    labelContent={
                        <Label required showColon>
                            {t('EMAIL')}
                        </Label>
                    }
                    style={{ textAlign: 'right', marginRight: '2rem' }}
                >
                    <Input
                        required
                        aria-label="SAP email"
                        aria-required
                        {...register('email')}
                        valueState={errors.email ? 'Negative' : 'None'}
                        valueStateMessage={<Text>{t('INVALID_EMAIL_MESSAGE')}</Text>}
                        type="Email"
                        placeholder={t('EMAIL')}
                        style={{ height: '30px' }}
                    />
                </FormItem>
            </FlexBox>
        </Form>
    );

    return (
        <div>
            <Dialog
                open
                onKeyDown={onDialogKeydown}
                style={{ width: '30%' }}
                headerText={t('SIGNUP')}
                footer={
                    <FlexBox style={{ gap: 8, width: '100%' }} justifyContent="End" alignItems="Center">
                        <Button data-testid="submit-btn" design="Emphasized" onClick={onSubmit}>
                            {t('SIGNUP')}
                        </Button>
                        <Button data-testid="cancel-btn" onClick={onClose}>
                            {t('CANCEL_BUTTON')}
                        </Button>
                    </FlexBox>
                }
            >
                <Title level="H6" style={{ marginBottom: ThemingParameters.sapContent_Margin_Small }}>
                    <Text>{t('SIGNUP_DIALOG_TITLE')}</Text>
                </Title>

                {signUpForm}
            </Dialog>

            <MessageBox open={isOpen} type={signUpMessage.type} titleText={t(signUpMessage.title)} onClose={toggle}>
                {signUpMessage.statusCode === 404 ? (
                    <>
                        <Text>
                            {t(signUpMessage.subTitle)}{' '}
                            <Link href="https://www.sap.com/registration/contact.html" target="_blank">
                                {t('HELP_LINK_TEXT')}
                            </Link>
                        </Text>{' '}
                    </>
                ) : (
                    <Text>{t(signUpMessage.subTitle)}</Text>
                )}
            </MessageBox>
        </div>
    );
};

export default SignUp;
